import styled from './theme/styled.js';
import { token, tokenNumber } from './theme/tokens.js';

export const CLOSE_TRANSITION_MS = 300;

export const CartWrapper = styled.div(({ theme, isOpen }) => [
    {
        // external behavior
        position: 'fixed',
        padding: `${token(theme, 'space.panelPaddingVertical')} clamp(${token(theme, 'space.panelPaddingHorizontal')}, 5%, 5px)`,
        top: token(theme, 'size.panelTop'),
        right: 0,
        width: token(theme, 'size.panelWidth'),
        maxWidth: '95%',
        boxSizing: 'border-box',
        // Can't use `token()` here: this is arithmetic, and `calc()` inside a
        // `max-height` with two nested `var()`s is more indirection than it's worth.
        maxHeight: `calc(100vh - ${tokenNumber(theme, 'size.panelTop') + tokenNumber(theme, 'space.panelPaddingVertical')}px)`,
        zIndex: token(theme, 'zIndex.panel'),

        transform: !isOpen && 'translateX(105%)',
        transition: `transform ${CLOSE_TRANSITION_MS}ms ease-in-out`,

        boxShadow: token(theme, 'shadow.panel'),
        borderRadius: `${token(theme, 'radius.panel')} 0 0 ${token(theme, 'radius.panel')}`,
        border: `1px solid ${token(theme, 'color.border')}`,
        borderRight: 0,
        overflowY: 'auto',
        // Media query bounds cannot contain `var()`, so this reads the resolved
        // token directly. A host overriding panel geometry from CSS alone will not
        // move this breakpoint — `data-config.tokens` is the path that does.
        [`@media(max-height: ${tokenNumber(theme, 'size.panelBreakHeight')}px)`]: {
            top: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            maxWidth: token(theme, 'size.panelWidth'),
            borderRadius: 0,
            maxHeight: 'unset',
            overscrollBehavior: 'contain',
        },

        // internal behavior
        fontFamily: token(theme, 'font.stack'),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        // gap: 32,
        background: token(theme, 'color.surface'),
        color: token(theme, 'color.text'),

        // Fix quantity input alignment
        input: {
            '-webkit-box-sizing': 'border-box',
            '-moz-box-sizing': 'border-box',
            boxSizing: 'border-box',
        },
        sup: {
            verticalAlign: 'super',
        },
        p: {
            marginBottom: 10,
        },
    },
]);

export const Header = styled.header(({ theme }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    alignSelf: 'flex-start',
    paddingBottom: token(theme, 'space.panelPaddingVertical'),
}));

export const Banner = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: token(theme, 'space.smallGap'),

    fontFamily: token(theme, 'font.bold'),

    width: '100%',
    backgroundColor: token(theme, 'color.highlightBackground'),

    margin: `0 calc(-1 * clamp(${token(theme, 'space.panelPaddingHorizontal')}, 5%, 5px))`, // Let child element span full parent width to apply backgound/border
    padding: `0.5rem clamp(${token(theme, 'space.panelPaddingHorizontal')}, 5%, 5px)`, // Reset parent padding after margin change
}));

export const Footer = styled.footer(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    alignSelf: 'flex-end',
    width: '100%',
    gap: '1rem',
    paddingTop: token(theme, 'space.panelPaddingVertical'),
}));

/**
 * Uses `<h2>` to defer `h1` priority to the host's heading
 * without assuming anything about the rest of the page's structure.
 */
export const Title = styled.h2(({ theme }) => ({
    fontSize: token(theme, 'fontSize.title'),
    fontFamily: token(theme, 'font.heading'),
    fontWeight: 500,
    margin: 0,
}));

export const CheckoutButton = styled.button(({ theme }) => ({
    padding: `${token(theme, 'space.panelPaddingVertical')} 0px`,
    borderRadius: token(theme, 'radius.button'),
    background: token(theme, 'color.accent'),
    width: '100%',
    display: 'inline-block',
    textAlign: 'center',
    color: token(theme, 'color.onAccent'),
    fontFamily: token(theme, 'font.bold'),
    fontSize: token(theme, 'fontSize.large'),
    border: 'none',
    cursor: 'pointer',
    '&:disabled': {
        opacity: 0.5,
        cursor: 'unset',
    },
}));

export const CloseButton = styled.button(({ theme }) => ({
    padding: 0,
    color: token(theme, 'color.textMuted'),
    background: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    border: 'none',
    fontSize: token(theme, 'fontSize.fine'),
    cursor: 'pointer',
}));

export const HideArrow = styled.span(({ theme }) => ({
    color: token(theme, 'color.accent'),
    fontSize: '1.5em',
    marginBottom: 4,
}));

export const PaymentOptionsImage = styled.img(({ theme }) => ({
    height: 'fit-content',
    maxWidth: '100%',
    width: token(theme, 'size.paymentImage'),
    marginBottom: 24,
}));

export const DeleteButton = styled.button(({ theme }) => ({
    background: 'none',
    border: 'none',
    color: token(theme, 'color.textMuted'),
    fontSize: 13,
    fontFamily: token(theme, 'font.body'),
    cursor: 'pointer',
    padding: '0 4px',
    textDecoration: 'underline',
    '&:hover': {
        opacity: 0.7,
    },
}));
